import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Leaf, LayoutDashboard, FlaskConical, ClipboardCheck, Calendar, CreditCard, LogOut, Menu, X, MessageCircle } from "lucide-react";
import { useState } from "react";
import { useUnreadMessages } from "@/hooks/useUnreadMessages";

const navItems = [
  { to: "/app", label: "Painel", icon: LayoutDashboard, end: true },
  { to: "/app/protocolos", label: "Protocolos", icon: ClipboardCheck },
  { to: "/app/check-in", label: "Check-in", icon: Leaf },
  { to: "/app/exames", label: "Exames", icon: FlaskConical },
  { to: "/app/agendamentos", label: "Agendamentos", icon: Calendar },
  { to: "/app/chat", label: "Chat", icon: MessageCircle },
  { to: "/app/assinatura", label: "Assinatura", icon: CreditCard },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { total: unread } = useUnreadMessages(!!user);

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex">
      {/* Sidebar */}
      <aside
        className={`fixed md:sticky top-0 left-0 h-screen w-64 border-r border-border bg-card z-40 transform ${open ? "translate-x-0" : "-translate-x-full"} md:translate-x-0 transition-transform duration-200`}
        data-testid="app-sidebar"
      >
        <div className="h-16 flex items-center gap-2 px-6 border-b border-border">
          <Link to="/app" className="flex items-center gap-2" data-testid="sidebar-brand-link">
            <div className="w-8 h-8 rounded-sm bg-primary text-primary-foreground grid place-items-center">
              <Leaf className="w-4 h-4" />
            </div>
            <div className="leading-tight">
              <div className="brand-wordmark font-bold text-[12px] text-primary uppercase">Performance</div>
              <div className="text-[9px] text-gold overline">Farmacêutica</div>
            </div>
          </Link>
        </div>
        <nav className="p-3 space-y-1">
          {navItems.map(({ to, label, icon: Icon, end }) => {
            const showBadge = to === "/app/chat" && unread > 0;
            return (
              <NavLink
                key={to}
                to={to}
                end={end}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-sm text-sm font-medium transition-colors ${
                    isActive ? "bg-primary text-primary-foreground" : "text-foreground hover:bg-muted"
                  }`
                }
                data-testid={`nav-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              >
                <Icon className="w-4 h-4" />
                <span className="flex-1">{label}</span>
                {showBadge && (
                  <span
                    className="min-w-[20px] h-5 px-1.5 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold grid place-items-center"
                    data-testid="chat-unread-badge"
                  >
                    {unread > 99 ? "99+" : unread}
                  </span>
                )}
              </NavLink>
            );
          })}
        </nav>
        <div className="absolute bottom-0 left-0 right-0 p-3 border-t border-border">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-9 h-9 rounded-full bg-accent text-accent-foreground grid place-items-center font-semibold">
              {(user?.name || "U").charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate" data-testid="sidebar-user-name">{user?.name}</div>
              <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
            </div>
            <button
              onClick={onLogout}
              className="p-2 rounded-sm hover:bg-muted transition-colors"
              data-testid="sidebar-logout-btn"
              title="Sair"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Overlay mobile */}
      {open && (
        <div
          className="fixed inset-0 bg-black/40 z-30 md:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Main */}
      <div className="flex-1 min-w-0">
        <header className="sticky top-0 z-20 bg-white/80 backdrop-blur-xl border-b border-black/5">
          <div className="h-16 flex items-center justify-between px-4 md:px-8">
            <button
              className="md:hidden p-2 rounded-sm hover:bg-muted"
              onClick={() => setOpen(!open)}
              data-testid="sidebar-toggle-btn"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <div className="overline text-muted-foreground hidden md:block">Acompanhamento farmacêutico contínuo</div>
            <div className="flex items-center gap-3">
              <span className="hidden sm:inline text-sm text-muted-foreground">Olá, {user?.name?.split(" ")[0]}</span>
            </div>
          </div>
        </header>
        <main className="p-4 md:p-8">{children}</main>
      </div>
    </div>
  );
}
